import {
  Banner,
  Box,
  Button,
  DefaultCellContent,
  Divider,
  Flex,
  InlineLink,
  OptionType,
  SelectMenu,
  Spinner,
  Table,
  TableColumnType,
  Text,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { PlayButton } from '@fullstory/overlays-ui';
import { Event } from '@fullstory/proto/fs/api/segment';
import {
  ActivityMonitoring,
  ActivityMonitoringError,
  AuditEvent,
  DQMRow,
  VolumeTimeRange,
} from '@fullstory/proto/fs/services/dx-objects';
import { TrendFrequency } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { int64, Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { fullstorySelectors } from '@fullstory/redux';
import { generateRoutePath, ORG_ROUTE } from '@fullstory/routes';
import { useCompleteSessionsReplayFilters } from '@fullstory/search-shared-ui';
import { shortNumber } from '@fullstory/util/fmt';
import { isOnline as getIsOnline } from '@fullstory/util/session';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { MatchingUserRow, useMatchingUser } from '../../hooks/useMatchingUser';
import { getVolumePoints } from '../../utils';
import { ActivityMonitoringTrendsChart } from '../ActivityTabTrendsChart';
import { AuditLog } from '../AuditLog';

const timestampToDate = (t: timestamp | null | undefined) =>
  t ? new Date(t.toString()) : new Date(0);

type ActivityTabProps = {
  activityMonitoring: ActivityMonitoring | null;
  activityMonitoringError: ActivityMonitoringError | null;
  timeRange: VolumeTimeRange;
  onTimeRangeChange: (timeRange: VolumeTimeRange) => void;
  name?: string;
  description?: string;
  searchEvent?: Event;
  objectType: string;
  monitoringStart: number;
  auditHistory: AuditEvent[] | null;
  eventSupport: DQMRow.EventSupport;
  showDataDirect?: boolean;
};

interface SessionPlaylistRowProps {
  searchEvent: Event;
  orgId: string;
  objectType: string;
  lastSeen: timestamp | null;
}

const SessionPlaylistRow: React.FC<SessionPlaylistRowProps> = ({
  searchEvent,
  orgId,
  objectType,
  lastSeen,
}) => {
  const refreshInterval = 5 * 60 * 1000; // milliseconds
  const { matchingUsers, loading } = useMatchingUser({
    searchEvent,
    lastSeen,
  });
  const useNowDate = (refreshInterval: number) => {
    const [nowDate, setNowDate] = useState(new Date());
    useEffect(() => {
      const intervalId = setInterval(() => {
        setNowDate(new Date());
      }, refreshInterval);
      return () => clearInterval(intervalId);
    }, [refreshInterval]);
    return nowDate;
  };
  const { completeSessionsOverride } = useCompleteSessionsReplayFilters(false);
  const nowProp = useNowDate(refreshInterval);
  const cols: TableColumnType<MatchingUserRow>[] = [
    {
      key: 'play-button',
      renderCell({ sessionLink, date, isWatched }) {
        const now = moment(nowProp);
        const lastEventTime = moment(Timestamp.toMillis(date));

        const isOnline = getIsOnline(now, lastEventTime, completeSessionsOverride);
        const sessionLinkHref =
          sessionLink.route.length > 0
            ? `${generateRoutePath(ORG_ROUTE, { orgId })}/${sessionLink.route.join(
                '/',
              )}?autoselect=true`
            : '';
        return (
          <PlayButton
            isOnline={isOnline}
            isWatched={isWatched}
            newTab
            isClip={false}
            to={{
              pathname: sessionLinkHref,
              state: sessionLink.state,
            }}
            testId={`${objectType.toLowerCase()}-definition-activity-tab-watch-session`}
          />
        );
      },
      align: 'left',
      width: '10%',
    },
    {
      key: 'url',
      renderCell({ url }) {
        return (
          <DefaultCellContent
            label={url}
            textStyles={{
              appearance: 'bodySmall',
              noWrap: true,
              color: 'default',
              tag: 'div',
              title: url,
            }}
          />
        );
      },
      label: intl.formatMessage({ defaultMessage: 'URL', id: 'bWjdfa' }),
      align: 'left',
      width: '70%',
    },
    {
      key: 'date',
      renderCell({ date }) {
        return <TimestampWithDefault timestamp={date} testId="most-recent-session-date" />;
      },
      label: intl.formatMessage({ defaultMessage: 'Date', id: 'P7PLVj' }),
      align: 'left',
    },
  ];
  return (
    <>
      {matchingUsers && matchingUsers.length > 0 && matchingUsers[0] ? (
        <Box data-component="SessionPlaylistRow" paddingTop={16}>
          <Box paddingBottom={8}>
            <Text appearance="bodyLargeBold">
              <FormattedMessage
                defaultMessage="Most recent session that includes this {objectType}"
                id="5Gr1eB"
                values={{ objectType }}
              />
            </Text>
          </Box>
          <Table
            borders="head-bottom"
            rowKeyAccessor="url"
            headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
            fixed
            columns={cols}
            rows={[matchingUsers[0]]}
          />
        </Box>
      ) : loading ? (
        <Spinner />
      ) : null}
    </>
  );
};

const DataDirectTimeRangeOptions: OptionType<VolumeTimeRange>[] = [
  {
    value: VolumeTimeRange.DAY_7,
    label: intl.formatMessage({ defaultMessage: 'Past 7 days', id: 'mbbNJS' }),
    key: VolumeTimeRange.DAY_7.toString(),
  },
  {
    value: VolumeTimeRange.HOUR_72,
    label: intl.formatMessage({ defaultMessage: 'Past 72 hours', id: 'NS+bUp' }),
    key: VolumeTimeRange.HOUR_72.toString(),
  },
];

const TimeRangeOptions: OptionType<VolumeTimeRange>[] = [
  {
    value: VolumeTimeRange.DAY_30,
    label: intl.formatMessage({ defaultMessage: 'Past 30 days', id: 'OIM20v' }),
    key: VolumeTimeRange.DAY_30.toString(),
  },
  {
    value: VolumeTimeRange.DAY_7,
    label: intl.formatMessage({ defaultMessage: 'Past 7 days', id: 'mbbNJS' }),
    key: VolumeTimeRange.DAY_7.toString(),
  },
  {
    value: VolumeTimeRange.HOUR_72,
    label: intl.formatMessage({ defaultMessage: 'Past 72 hours', id: 'NS+bUp' }),
    key: VolumeTimeRange.HOUR_72.toString(),
  },
];

const timeRangeToFrequency = {
  [VolumeTimeRange.DAY_30]: TrendFrequency.TREND_FREQUENCY_DAILY,
  [VolumeTimeRange.DAY_7]: TrendFrequency.TREND_FREQUENCY_DAILY,
  [VolumeTimeRange.HOUR_72]: TrendFrequency.TREND_FREQUENCY_HOURLY,
};

export const ActivityTab = ({
  activityMonitoring,
  activityMonitoringError,
  onTimeRangeChange,
  timeRange,
  searchEvent,
  name,
  description,
  objectType,
  monitoringStart,
  auditHistory,
  eventSupport,
  showDataDirect,
}: ActivityTabProps) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);

  const volume = useMemo(() => {
    if (!activityMonitoring?.volume) {
      return [];
    }
    return getVolumePoints(activityMonitoring.volume);
  }, [activityMonitoring]);

  return (
    <Box data-component="ActivityTab">
      <Box paddingBottom={24}>
        {name && (
          <Box paddingBottom={24}>
            <Text appearance="heading3">{name}</Text>
            {description && (
              <Box paddingTop={16}>
                <Text appearance="bodyMedium" color="secondary">
                  {description}
                </Text>
              </Box>
            )}
          </Box>
        )}
        <Flex column gap={8} alignItems="stretch">
          <Text appearance="heading3">
            <FormattedMessage defaultMessage="Event volume" id="WqRRne" />
          </Text>
          {eventSupport !== DQMRow.EventSupport.EVENT_SUPPORT_UNSUPPORTED && (
            <Box marginBottom={24} paddingRight={56}>
              <Text appearance="bodyMedium" color="default">
                <FormattedMessage defaultMessage="We now offer" id="430pw0" />{' '}
                <InlineLink
                  href="https://help.fullstory.com/hc/en-us/articles/16433057443479"
                  newTab
                >
                  <FormattedMessage defaultMessage="data quality monitoring" id="beNLLY" />
                </InlineLink>
                <FormattedMessage
                  defaultMessage=". This shows the number of events during a specific time period. We raise an issue when no events occur for 72 hours."
                  id="q0qfNV"
                />{' '}
              </Text>
            </Box>
          )}
          {activityMonitoring ? (
            <>
              {eventSupport === DQMRow.EventSupport.EVENT_SUPPORT_UNSUPPORTED ? (
                <Box>
                  <Banner
                    title={intl.formatMessage({
                      defaultMessage: 'Normally, a chart would appear in this area',
                      id: 'L+3uSy',
                    })}
                    description={intl.formatMessage({
                      defaultMessage:
                        'No action needed. Data quality monitoring does not support this event type.',
                      id: 'laE7Fz',
                    })}
                  />
                </Box>
              ) : (
                <Box>
                  <Box>
                    <Flex justifyContent="space-between">
                      <Box width={300}>
                        <SelectMenu
                          fullWidth
                          variant="fill-builder"
                          selected={TimeRangeOptions.find(option => option.value == timeRange)}
                          onChange={option =>
                            option?.value != null ? onTimeRangeChange(option.value) : null
                          }
                          options={showDataDirect ? DataDirectTimeRangeOptions : TimeRangeOptions}
                          testId={`${objectType.toLowerCase()}-definition-activity-tab-date-range-filter`}
                        />
                      </Box>
                      <Flex column gap={8} alignItems="flex-end">
                        <Text appearance="bodySmall">
                          <FormattedMessage defaultMessage="Total events" id="R9CnnE" />
                        </Text>
                        <Text appearance="bodyLargeBold" color="emphasized">
                          {shortNumber(int64.toNumber(activityMonitoring.totalEventCount))}
                        </Text>
                      </Flex>
                    </Flex>
                  </Box>
                  <Box padding={[20, 20, 12, 20]} marginTop={32}>
                    <ActivityMonitoringTrendsChart
                      volume={volume}
                      frequency={timeRangeToFrequency[timeRange]}
                    />
                  </Box>
                </Box>
              )}
              {searchEvent && activityMonitoring.firstSeen !== null && (
                <Box marginBottom={8}>
                  <SessionPlaylistRow
                    searchEvent={searchEvent}
                    orgId={orgId}
                    objectType={objectType}
                    lastSeen={activityMonitoring.lastSeen}
                  />
                </Box>
              )}
              <Box marginBottom={8}>
                <Text appearance="bodyLargeBold">
                  <FormattedMessage defaultMessage="Details" id="Lv0zJu" />
                </Text>
                <Text appearance="bodyMedium" color="secondary">
                  <FormattedMessage
                    defaultMessage="Activity monitoring started in {monitoringStart, date, ::MMMM, y}."
                    id="qsrZ4X"
                    values={{ monitoringStart }}
                  />
                </Text>
              </Box>
              <Flex justifyContent="space-between">
                <Text appearance="bodyMedium" color="secondary">
                  <FormattedMessage defaultMessage="First seen" id="tJZ3DG" />
                </Text>
                <TimestampWithDefault
                  timestamp={activityMonitoring.firstSeen}
                  testId="first-seen"
                />
              </Flex>
              <Divider />
              <Flex justifyContent="space-between">
                <Text appearance="bodyMedium" color="secondary">
                  <FormattedMessage defaultMessage="Last seen" id="PO83gc" />
                </Text>
                <TimestampWithDefault timestamp={activityMonitoring.lastSeen} testId="last-seen" />
              </Flex>
            </>
          ) : activityMonitoringError ? (
            <ErrorState onReload={() => onTimeRangeChange(timeRange)} />
          ) : null}
          {auditHistory && (
            <Box paddingTop={16}>
              <Box paddingBottom={8}>
                <Text appearance="bodyLargeBold">
                  <FormattedMessage defaultMessage="Element history" id="BvgEI6" />
                </Text>
                <Text appearance="bodyMedium" color="secondary">
                  <FormattedMessage
                    defaultMessage="Element history tracking started in December 2023."
                    id="q00XAA"
                  />
                </Text>
              </Box>
              <AuditLog data={auditHistory} />
            </Box>
          )}
        </Flex>
      </Box>
    </Box>
  );
};

interface TimestampWithDefaultProps {
  timestamp: timestamp | null;
  testId?: string;
}

const TimestampWithDefault: React.FC<TimestampWithDefaultProps> = ({ timestamp, testId }) => {
  const { color, content } = timestamp
    ? {
        color: 'default' as const,
        content: (
          <FormattedDate
            value={timestampToDate(timestamp)}
            month="long"
            day="numeric"
            year="numeric"
          />
        ),
      }
    : {
        color: 'secondary' as const,
        content: <FormattedMessage defaultMessage="N/A" id="PW+sL4" />,
      };
  return (
    <Text
      data-component="TimestampWithDefault"
      appearance="bodyMedium"
      color={color}
      testId={testId}
    >
      {content}
    </Text>
  );
};
interface ErrorStateProps {
  onReload: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ onReload }) => (
  <Box data-component="ErrorState" marginTop={24} marginBottom={32}>
    <Flex column>
      {/* TODO: add error image https://www.figma.com/file/03W1QE0EETIO1vRzJLRtu4/DQM---Data-Quality-Monitoring?node-id=1916-42597&t=KS4qaTpWRPV9pklg-0 */}
      <Box marginBottom={12}>
        <Text appearance="heading1">
          <FormattedMessage defaultMessage="Something's wrong" id="WBubXY" />
        </Text>
      </Box>
      <Box marginBottom={24}>
        <Text appearance="bodyMedium">
          <FormattedMessage
            defaultMessage="Your information is currently unavailable. Try again later."
            id="LaWuvK"
          />
        </Text>
      </Box>
      <Button onClick={onReload}>
        <FormattedMessage defaultMessage="Reload" id="fdCS5/" />
      </Button>
    </Flex>
  </Box>
);
